/**
 * Project List page — /projects
 * From UI_UX_SPEC §3.1: Portfolio overview for returning users
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, FolderOpen } from "lucide-react";
import { api } from "../lib/api.ts";
import { ErrorMessage } from "../components/shared/ErrorMessage.tsx";
import { EmptyState } from "../components/shared/EmptyState.tsx";
import { COUNTRY_META } from "../lib/constants.ts";
import { CountryBadge } from "../components/shared/CountryBadge.tsx";
import { formatCapacity } from "../lib/utils.ts";
import { HealthRing } from "../components/shared/HealthRing.tsx";
import { Tooltip } from "../components/shared/Tooltip.tsx";
import type { ProjectListItem } from "../types/project.ts";

export function ProjectList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.getProjects()
      .then((data) => { setProjects(data); })
      .catch((err) => { console.error("Failed to load projects:", err); setError(err instanceof Error ? err.message : "Failed to load projects"); })
      .finally(() => { setLoading(false); });
  }, []);

  const filtered = projects.filter((p) => {
    if (filter !== "all" && p.country_code !== filter) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    active: projects.filter((p) => p.status !== "archived" && p.status !== "operational").length,
    countries: new Set(projects.map((p) => p.country_code)).size,
    alerts: projects.reduce((s, p) => s + p.active_alerts, 0),
    avgReadiness: projects.length > 0
      ? Math.round(projects.reduce((s, p) => s + p.readiness_pct, 0) / projects.length)
      : 0,
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-[22px] font-bold" style={{ letterSpacing: "-0.02em" }}>Projects</h1>
        <button
          onClick={() => navigate("/projects/new")}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-on-solid rounded-xl text-[16px] font-medium hover:bg-brand/90 transition-colors"
        >
          <Plus size={14} />
          New project
        </button>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          { label: "ACTIVE PROJECTS", value: stats.active, tip: "Projects not yet archived or operational" },
          { label: "COUNTRIES", value: stats.countries, tip: "Distinct countries across your portfolio" },
          { label: "PENDING ALERTS", value: stats.alerts, tip: "Regulatory alerts requiring action" },
          { label: "AVG. READINESS", value: `${stats.avgReadiness}%`, tip: "Average field completion across all projects" },
        ].map((s) => (
          <Tooltip key={s.label} text={s.tip} position="bottom">
            <div className="bg-surface-secondary rounded-xl px-3.5 py-2.5 w-full">
              <div className="text-[12px] font-medium text-text-tertiary uppercase tracking-wider">{s.label}</div>
              <div className="text-[26px] font-medium mt-0.5">
                {loading ? <span className="inline-block w-8 h-7 bg-border/50 rounded animate-pulse" /> : s.value}
              </div>
            </div>
          </Tooltip>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-4">
        <div className="flex items-center gap-2 overflow-x-auto w-full sm:w-auto">
          {["all", ...Object.keys(COUNTRY_META)].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-full text-[14px] font-medium transition-colors whitespace-nowrap ${
                filter === f
                  ? "bg-brand text-on-solid"
                  : "bg-surface-tertiary text-text-secondary hover:bg-border"
              }`}
            >
              {f === "all" ? "All" : COUNTRY_META[f as keyof typeof COUNTRY_META]?.name}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="relative w-full sm:w-auto">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary" />
          <input
            type="text"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 pr-3 py-1.5 bg-surface border border-border rounded-xl text-[14px] w-full sm:w-52 focus:outline-none focus:ring-1 focus:ring-brand/30"
          />
        </div>
      </div>

      {/* Error state */}
      {error && <ErrorMessage message={error} onRetry={() => window.location.reload()} />}

      {/* Project cards */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-surface border border-border rounded-2xl p-4 animate-pulse">
              <div className="h-4 bg-surface-tertiary rounded w-1/3 mb-3" />
              <div className="h-3 bg-surface-tertiary rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-surface border border-border rounded-2xl p-4">
          {projects.length === 0 ? (
            <EmptyState
              icon={FolderOpen}
              title="No projects yet"
              description="Create your first project to get started."
              actionLabel="Create project"
              onAction={() => navigate("/projects/new")}
            />
          ) : (
            <EmptyState
              icon={Search}
              title="No matches"
              description="No projects match your current filter."
            />
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectCard({ project }: { project: ProjectListItem }) {
  const capacity = project.capacity_kw ? formatCapacity(project.capacity_kw) : "—";

  return (
    <Link
      to={`/projects/${project.id}`}
      className="block bg-surface border border-border rounded-2xl p-4 card-interactive hover:border-border-secondary no-underline text-text-primary"
    >
      {/* Row 1: Name + metadata */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <CountryBadge code={project.country_code} />
        <span className="text-[17px] font-medium truncate min-w-0">{project.name}</span>
        <span className="text-[14px] text-text-tertiary ml-auto">{capacity}</span>
        <span className="text-[13px] text-text-tertiary capitalize">{project.status.replace("_", " ")}</span>
      </div>

      {/* Row 2: Readiness + stats */}
      <div className="flex items-center gap-3 sm:gap-5">
        <HealthRing percentage={project.readiness_pct} size={44} tooltip={`${project.readiness_pct}% of required fields filled`} />
        <div className="flex gap-3 sm:gap-6 text-[14px] text-text-secondary flex-wrap">
          <Tooltip text="Required data fields completed">
            <div>
              <span className="font-medium text-text-primary">{project.fields_complete}</span>
              <span>/{project.fields_total} fields</span>
            </div>
          </Tooltip>
          <Tooltip text="Documents uploaded and processed">
            <div>
              <span className="font-medium text-text-primary">{project.docs_complete}</span>
              <span> docs</span>
            </div>
          </Tooltip>
          <Tooltip text="License applications tracked">
            <div>
              <span className="font-medium text-text-primary">{project.licenses_total}</span>
              <span> licenses</span>
            </div>
          </Tooltip>
        </div>
      </div>

      {/* Row 3: License progress bar */}
      <Tooltip text="Green = approved, yellow = in preparation" position="bottom">
      <div className="mt-3 flex h-1.5 rounded-full overflow-hidden bg-surface-tertiary w-full">
        {project.licenses_approved > 0 && (
          <div
            className="bg-brand"
            style={{ width: `${(project.licenses_approved / project.licenses_total) * 100}%` }}
          />
        )}
        {project.licenses_preparing > 0 && (
          <div
            className="bg-warning"
            style={{ width: `${(project.licenses_preparing / project.licenses_total) * 100}%` }}
          />
        )}
      </div>
      </Tooltip>

      {/* Row 4: Bottom metadata */}
      {(project.active_alerts > 0 || project.top_nudge) && (
        <div className="flex items-center gap-2 mt-2.5 text-[13px]">
          {project.active_alerts > 0 && (
            <span className="px-2 py-0.5 bg-danger-light text-danger rounded-full font-medium">
              {project.active_alerts} alert{project.active_alerts > 1 ? "s" : ""}
            </span>
          )}
          {project.top_nudge && (
            <span className="text-ai truncate flex-1">{project.top_nudge}</span>
          )}
        </div>
      )}
    </Link>
  );
}
